// "Finding the best available lawyer…" screen — shown after the consultation
// payment is authorized while we broadcast the request to matched lawyers and
// wait for the first one to accept (socket `call-accepted`).
import { useEffect, useState } from "react";
import logo from "../../assets/images/logo.png";
import lawyerAvatar from "../../assets/images/hireNow.png";
import "./findingLawyer.css";

const STEPS = [
  { icon: "bi-person-vcard", label: "Checking lawyer available" },
  { icon: "bi-telephone-outbound", label: "Preparing your call" },
  { icon: "bi-clipboard-check", label: "Matching your case" },
  { icon: "bi-patch-check", label: "Verifying qualifications" },
];

const fmt = (total) => {
  const m = String(Math.floor(total / 60)).padStart(2, "0");
  const s = String(total % 60).padStart(2, "0");
  return `${m}:${s}`;
};

// How long we look for a lawyer before giving up. Shown here as the estimated
// connection countdown, and used by ConsultationCall as the search timeout.
export const SEARCH_SECONDS = 30;

/**
 * @param {boolean}  found      true once the initiate API returned matched lawyers
 * @param {boolean}  timedOut  true once the search expired with no lawyer accepting
 * @param {function} onCancel  cancel the search and release the request
 * @param {function} onClose   dismiss the timed-out screen
 */
export default function FindingLawyer({
  found = false,
  timedOut = false,
  onCancel,
  onClose,
}) {
  const [seconds, setSeconds] = useState(0);
  // Reveal the checklist ticks one by one for a "working" feel.
  const [doneSteps, setDoneSteps] = useState(0);

  useEffect(() => {
    if (timedOut) return;
    const tick = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(tick);
  }, [timedOut]);

  useEffect(() => {
    // Hold the last step spinning until lawyers are actually found, then finish
    // the checklist — so the ticks track the real "finding → found" transition.
    const cap = found ? STEPS.length : STEPS.length - 1;
    if (doneSteps >= cap) return;
    const id = setTimeout(() => setDoneSteps((n) => n + 1), 700);
    return () => clearTimeout(id);
  }, [doneSteps, found]);

  // 6 avatars positioned around the orbit ring, all showing the same static
  // lawyer photo — this is a "lawyers online" presentational effect, not tied
  // to the real matched pool (which can be as small as one lawyer).
  const orbit = Array.from({ length: 6 });

  const eta = Math.max(0, SEARCH_SECONDS - seconds);

  // The countdown ran out with nobody accepting — the search has been stopped
  // and the held payment released by the parent.
  if (timedOut) {
    return (
      <div className="fl-overlay">
        <div className="fl-wrap">
          <div className="fl-busy">
            <div className="fl-busy-icon"><i className="bi bi-hourglass-bottom" /></div>
            <h2 className="fl-title">All lawyers are busy at the moment</h2>
            <p className="fl-subtitle">
              We couldn&apos;t connect you to a lawyer within {SEARCH_SECONDS} seconds.
              Please try again in a little while.
            </p>
            <div className="fl-busy-note">
              <i className="bi bi-shield-check" /> Your card was not charged.
            </div>
            <button type="button" className="fl-busy-btn" onClick={onClose}>
              <i className="bi bi-arrow-left me-1" /> Back to consultation
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fl-overlay">
      <div className="fl-wrap">
        <h2 className="fl-title">
          {found ? "Available lawyers found!" : "Finding the best available lawyer..."}
        </h2>
        <p className="fl-subtitle">
          {found
            ? "Connecting you to the first available lawyer…"
            : "Please wait while we search for available lawyers"}
        </p>

        {found && (
          <div className="fl-found-badge">
            <i className="bi bi-check-circle-fill" />
            100+ lawyers found — ringing now…
          </div>
        )}

        <div className="fl-card">
          <div className="fl-left">
            <ul className="fl-steps">
              {STEPS.map((s, i) => (
                <li key={s.label} className={i < doneSteps ? "fl-step fl-step-done" : "fl-step"}>
                  <span className="fl-step-icon"><i className={`bi ${s.icon}`} /></span>
                  <span className="fl-step-label">{s.label}</span>
                  <span className="fl-step-check">
                    {i < doneSteps ? <i className="bi bi-check2" /> : <span className="fl-step-spin" />}
                  </span>
                </li>
              ))}
            </ul>

            <div className="fl-stats">
              <div className="fl-stat-row">
                <span className="fl-stat-label"><i className="bi bi-people-fill" /> Lawyers Available</span>
                <span className="fl-stat-value">
                  {found ? "100+" : <span className="fl-step-spin" />}
                </span>
              </div>
              <div className="fl-stat-row">
                <span className="fl-stat-label"><i className="bi bi-clock-history" /> Estimated Connection Time</span>
                <span className="fl-stat-value">{fmt(eta)}</span>
              </div>
            </div>
          </div>

          <div className="fl-right">
            <div className="fl-radar">
              <span className="fl-ring fl-ring-1" />
              <span className="fl-ring fl-ring-2" />
              <span className="fl-ring fl-ring-3" />
              <div className="fl-orbit">
                {orbit.map((_, i) => (
                  <span
                    key={i}
                    className="fl-avatar"
                    style={{ "--i": i, "--n": orbit.length }}
                  >
                    <img src={lawyerAvatar} alt="Lawyer" />
                  </span>
                ))}
              </div>
              <div className="fl-core">
                <img src={logo} alt="Legal Platform" />
              </div>
            </div>
            <div className="fl-secure"><i className="bi bi-shield-lock-fill" /> Your call is secure and confidential</div>
          </div>
        </div>

        <button type="button" className="fl-cancel" onClick={onCancel}>
          <i className="bi bi-x-circle me-1" /> Cancel search
        </button>
        <div className="fl-elapsed">Searching… {fmt(seconds)}</div>
      </div>
    </div>
  );
}
